import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { DynamicContinueButton } from '@dealstack/frontend/shared/components/DynamicContinueButton/DynamicContinueButton';
import { ButtonsApart } from '@dealstack/frontend/shared/components/fundament/Button/ButtonLayouts';
import {
	BackButton,
	SaveDraftButton,
} from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Divider } from '@dealstack/frontend/shared/components/fundament/Divider/Divider';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { NotificationBox } from '@dealstack/frontend/shared/components/NotificationBox/NotificationBox';
import React, { useState } from 'react';
import { generatePath, Navigate, Route, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { MIPCreateSidebar } from './MIPCreateSidebar';
import { MIP_CREATE_ROUTES, useMIPCreateController } from './useMIPCreateController';
import { ForwardOnlySubstepMenu as SubstepMenuComponent } from '@dealstack/frontend/shared/components/SubstepMenu/ForwardOnlySubstepMenu';
import { GeneralDealInputs as MIPCreateGeneral } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/GeneralDealInputs/GeneralDealInputs';
import { Securities as MIPCreateSecurities } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/Securities/Securities';
import { PersonalData as MIPCreatePersonal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/PersonalData/PersonalData';
import { KYCRequirements as MIPCreateKYCRequirements } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCRequirements';
import { Contracts as MIPCreateContracts } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/Contracts/Contracts';
import { IndividualSchedule as MIPCreateSchedule } from '@dealstack/frontend/shared/components/IndividualSchedule/IndividualSchedule';
import { Investors as MIPInvestors } from '@dealstack/frontend/shared/components/Investors/Investors';
import { Collaborate as MIPCreateCollaborate } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/Collaborate/Collaborate';
import { DealSummary as MIPCreateSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/DealSummary/DealSummary';
import { MIPCreateContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/MIPCreateContext';
import { MIP_CREATE_PORTALS } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/mipCreatePortals';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry';
import {
	NoSaveStep,
	WizardStepRegistryProvider,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { DEAL_ROUTES, MIP_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';

interface Props {
	className?: string;
}

const MIPCreate = ({ className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const navigate = useNavigate();
	const [deleteDraftOpen, setDeleteDraftOpen] = useState(false);
	const mipCreateController = useMIPCreateController();
	const dealId = mipCreateController.dealId ?? '';
	const mipId = mipCreateController.mipId ?? '';

	const dealRoute = generatePath(ROUTES.DEAL, { dealId });
	const mipCreateRoute = generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.MIP}${MIP_ROUTES.CREATE}`, {
		dealId,
		mipId,
	});

	const handleDeleteMipDraft = () => {
		mipCreateController.deleteMipDraftRequest.mutate(undefined, {
			onSuccess: () => navigate(dealRoute),
			onError: () => {
				showToastV2({
					message: tMipCreate('mipDraft.delete.fail.message', {
						defaultValue: 'Could not delete draft',
					}),
					state: 'error',
				});
			},
		});
	};

	return (
		<div className={className}>
			{mipCreateController.isLoading ? <InlineLoader /> : null}
			<MIPCreateContext.Provider value={mipCreateController}>
				<WizardStepRegistryProvider registry={mipCreateController.stepRegistry}>
					<MIPCreateLeftWrapper>
						<MIPCreateSidebar onDeleteClick={() => setDeleteDraftOpen(true)} />
					</MIPCreateLeftWrapper>
					<MIPCreateRightWrapper>
						<MIPCreateHeaderWrapper>
							<MIPCreateSectionHeader>
								<div id={MIP_CREATE_PORTALS.SECTION_HEADER} />
								{!mipCreateController.isSavelessStep &&
								!mipCreateController.showSubStepsNavigation ? (
									<UIElementIdentifier
										identifier={TEST_ELEMENT_IDS.MIP_CREATE_SAVE_DRAFT_BUTTON}
									>
										<SaveDraftButton
											onClick={() => {
												void mipCreateController.onSaveDraft();
											}}
										/>
									</UIElementIdentifier>
								) : null}

								{mipCreateController.showSubStepsNavigation && (
									<div style={{ minWidth: 'fit-content' }}>
										<SubstepMenuComponent
											steps={mipCreateController.activeStepSubSteps}
											currentStep={mipCreateController.currentActiveSubStep}
											onNextClick={mipCreateController.onNextSection}
										/>
									</div>
								)}
							</MIPCreateSectionHeader>

							{!!mipCreateController.activeStep?.stateMessage && (
								<UIElementIdentifier
									identifier={TEST_ELEMENT_IDS.MIP_CREATE_NOTIFICATION_BOX}
								>
									<NotificationBox
										variant={mipCreateController.activeStep?.state}
										content={mipCreateController.activeStep?.stateMessage}
										pageName={`MIP-CREATE-${mipCreateController.activeStep?.step}`}
										messageId={`${mipCreateController.activeStep?.stateCode}`}
									/>
								</UIElementIdentifier>
							)}
						</MIPCreateHeaderWrapper>
						<MIPCreateContentWrapper>
							<SentryRoutes>
								<Route
									path={MIP_CREATE_ROUTES.GENERAL}
									element={<MIPCreateGeneral />}
								/>

								<Route
									path={MIP_CREATE_ROUTES.SECURITIES}
									element={<MIPCreateSecurities />}
								/>

								<Route
									path={MIP_CREATE_ROUTES.PERSONAL}
									element={<MIPCreatePersonal />}
								/>
								<Route
									path={`${MIP_CREATE_ROUTES.KYC}/*`}
									element={
										<MIPCreateKYCRequirements mipCreateRoute={mipCreateRoute} />
									}
								/>
								<Route
									path={MIP_CREATE_ROUTES.CONTRACTS}
									element={<MIPCreateContracts />}
								/>
								<Route
									path={MIP_CREATE_ROUTES.SCHEDULE}
									element={
										<NoSaveStep>
											<MIPCreateSchedule />
										</NoSaveStep>
									}
								/>
								<Route
									path={MIP_CREATE_ROUTES.INVESTORS}
									element={
										<NoSaveStep>
											<MIPInvestors
												navigate={mipCreateController.conditionallyNavigate}
												refreshStatuses={
													mipCreateController.refreshStatuses
												}
											/>
										</NoSaveStep>
									}
								/>
								<Route
									path={MIP_CREATE_ROUTES.COLLABORATE}
									element={<MIPCreateCollaborate />}
								/>
								<Route
									path={MIP_CREATE_ROUTES.SUMMARY}
									element={
										<NoSaveStep>
											<MIPCreateSummary />
										</NoSaveStep>
									}
								/>
								<Route
									path="*"
									element={
										<Navigate
											to={generatePath(
												`${ROUTES.DEAL}${DEAL_ROUTES.MIP}${MIP_ROUTES.CREATE}${MIP_CREATE_ROUTES.GENERAL}`,
												{
													dealId,
													mipId,
												}
											)}
										/>
									}
								/>
							</SentryRoutes>
						</MIPCreateContentWrapper>
						{!mipCreateController.hideBottomNavigation && (
							<MIPCreateFooterWrapper>
								<Divider />
								<ButtonsApart>
									<UIElementIdentifier
										identifier={TEST_ELEMENT_IDS.MIP_CREATE_BACK_BUTTON}
									>
										<BackButton
											onClick={mipCreateController.onPrevSection}
											disabled={!mipCreateController.prevSectionLink}
										/>
									</UIElementIdentifier>

									<UIElementIdentifier
										identifier={TEST_ELEMENT_IDS.MIP_CREATE_CONTINUE_BUTTON}
									>
										<DynamicContinueButton
											disabled={
												!mipCreateController.nextSectionLink ||
												mipCreateController.disableContinueButton
											}
											withPopover={mipCreateController.disableContinueButton}
											message={mipCreateController.popoverMessage ?? ''}
											onClick={mipCreateController.onNextSection}
										/>
									</UIElementIdentifier>
								</ButtonsApart>
							</MIPCreateFooterWrapper>
						)}
					</MIPCreateRightWrapper>

					<ConfirmDeleteDrawer
						opened={deleteDraftOpen}
						onClose={() => setDeleteDraftOpen(false)}
						title={tMipCreate('mipDraft.delete_drawer.title', {
							defaultValue: 'Delete Draft',
						})}
						inputLabel={tMipCreate('mipDraft.delete_drawer.inputLabel', {
							defaultValue: 'Enter the word above to confirm the deletion',
						})}
						inputPlaceholder={tMipCreate('mipDraft.delete_drawer.inputPlaceholder', {
							defaultValue: 'Enter word',
						})}
						hideAlertIcon={true}
						confirmDeleteText="delete"
						wordMismatchErrorMessage={tMipCreate(
							'mipDraft.delete_drawer.wordMismatchError',
							{ defaultValue: 'Input does not match' }
						)}
						deleteBtnText={tMipCreate('mipDraft.delete_drawer.confirmDelete', {
							defaultValue: 'Confirm delete',
						})}
						cancelBtnText={tMipCreate('mipDraft.delete_drawer.cancel', {
							defaultValue: 'Cancel',
						})}
						deleteLoading={mipCreateController.deleteMipDraftRequest.isPending}
						deleteSuccess={mipCreateController.deleteMipDraftRequest.isSuccess}
						handleDelete={handleDeleteMipDraft}
					/>
				</WizardStepRegistryProvider>
			</MIPCreateContext.Provider>
		</div>
	);
};

const MIPCreateContentWrapper = styled.div``;
const MIPCreateHeaderWrapper = styled.div``;
const MIPCreateFooterWrapper = styled.div``;
const MIPCreateLeftWrapper = styled.div``;
const MIPCreateRightWrapper = styled.div``;
const MIPCreateSectionHeader = styled.section`
	${SaveDraftButton}, .forwardOnlySubstepMenu {
		margin-left: 10em;
	}
`;

const Styled = styled(MIPCreate)`
	display: flex;
	flex-direction: row;
	flex-grow: 1;
	position: relative;

	${MIPCreateLeftWrapper} {
		display: flex;
		background-color: ${({ theme }) => theme.v2.color.neutral[200]};
		--static-width: 16rem;
		width: var(--static-width);
		min-width: var(--static-width);
		max-width: var(--static-width);
	}

	${MIPCreateRightWrapper} {
		justify-content: flex-start;
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		padding: 1rem 2rem;
		max-width: 100%;
		overflow: hidden;

		${MIPCreateHeaderWrapper} {
			margin-bottom: 1.5rem;

			${NotificationBox} {
				margin-top: 1em;
			}
		}

		${MIPCreateContentWrapper} {
			height: 100%;
		}

		${MIPCreateFooterWrapper} {
			${Divider} {
				margin-bottom: 1.5rem;
			}
			margin-top: auto;
		}
	}

	${MIPCreateSectionHeader} {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: 1.5rem;
	}
`;

export { Styled as MIPCreate };
